import { get } from 'svelte/store';
import { name, version } from '../../package.json';
import * as Sentry from '@sentry/svelte';
import { user_data, user_settings, home_data } from 'src/stores/account/user';

let _isInitialized = false;
let _sentryInstance = null;

// Get the Sentry instance. Sentry is initialized lazily
export const getInstance = () => {
	if (!_sentryInstance) {
		return null;
	}
	return _sentryInstance;
};

/**
 * Initialize Sentry on App start
 */
export const initSentry = () => {
	if (
		!import.meta.env.VITE_SENTRY_ENABLED || // .env configuration: is Sentry enabled
		_isInitialized ||
		getInstance()?.isInitialized() // Sentry is already initialized, prevent double initialization on HMR
	) {
		return;
	}
	_isInitialized = true;
	if (import.meta.env.MODE === 'development') {
		console.log('Initializing Sentry', `release: ${name}@${version}`);
	}

	// Initialize Sentry
	Sentry.init({
		release: `${name}@${version}`,
		environment: import.meta.env.MODE,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: ['localhost', /^https:\/\/*.shelly\.cloud\/*/],
		// Session Replay
		replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	});
};

export const setSentryRelatedData = () => {
	try {
		if (!import.meta.env.VITE_SENTRY_ENABLED) {
			console.warn('Sentry is not initialized yet.');

			return;
		}

		const user = { ...get(user_data) };
		const settings = get(user_settings);
		const homeData = get(home_data);

		delete user.password;
		delete user.refresh_token;
		delete user.token;

		Sentry.setUser(user);
		Sentry.setContext('Settings', settings);
		Sentry.setContext('Home Data', homeData);
	} catch (error) {
		console.error('Sentry failed setting additional user data.');
	}
};
